// @ts-check
// "Testar bot" (contrato de bots, seção 22.2): painel lateral com uma conversa simulada no estilo do WhatsApp, rodando
// no navegador o intérprete do bot com portas falsas, nos seis cenários (família atual identificada, telefone
// compartilhado, número desconhecido, lead novo, departamento fechado e sem ninguém disponível). Mostra o caminho, as
// variáveis (sensíveis como "informado") e o resumo que a equipe receberia; nada vai ao servidor nem ao WhatsApp.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getBot } from '../../data/bots.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { TabList } from '../../ui/tab-list.js';
import {
  BOT_TEST_SCENARIOS, BOT_TEST_STRONG_CODE, BOT_TEST_WEAK_DATA, answerBotTest, botInterpreter, lastBotOptions, resumeBotTest, startBotTest, testHandoffSummary,
  testVariables,
} from '../../lib/bot-test-simulator.js';
import { indexBotTree, walkBotTree } from '../../lib/bot-tree.js';
import { FlowSidePanel } from '../flows/flow-side-panel.js';
import { FlowNotice } from '../flows/flow-ui.js';
import { BotMessagePreview } from './bot-message-preview.js';
import { BotSelectField } from './bot-form-fields.js';
import { botBranchLabel, botReasonText, describeBotNode } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../../shared/bots/bot-summary.js').BotSummaryLookups} BotSummaryLookups
 * @typedef {import('../../lib/bot-test-simulator.js').BotTestState} BotTestState
 * @typedef {import('../../lib/bot-test-simulator.js').BotTestScenarioKey} BotTestScenarioKey
 * @typedef {import('../../lib/bot-test-simulator.js').BotChatMessage} BotChatMessage
 * @typedef {import('./bot-lookups.js').BotLookupData} BotLookupData
 */

/**
 * Modelo da prévia a partir do balão do bot.
 * @param {BotChatMessage} message @returns {import('../../lib/bot-message-limits.js').BotMessagePreviewModel}
 */
export function chatBubbleModel(message) {
  const isList = message.kind === 'LIST';
  const hasOptions = message.options.length > 0;
  return {
    kind: hasOptions ? (isList ? 'LIST' : 'BUTTONS') : 'TEXT', header: message.header ?? null, body: message.text, footer: message.footer ?? null,
    buttons: hasOptions && !isList ? message.options.map((option) => option.title) : [], listButton: isList ? message.listButtonLabel ?? null : null,
    rows: isList ? message.options.map((option) => ({ title: option.title, description: option.description ?? null })) : [], mediaKind: null, isDynamic: false,
  };
}

/**
 * Definições dos subbots usados (para o teste andar por eles), carregadas pela API.
 * @param {BotDefinition} definition @returns {Promise<Record<string, { definition: BotDefinition }>>}
 */
async function loadSubbots(definition) {
  /** @type {Set<string>} */
  const ids = new Set();
  walkBotTree(definition.nodes, (node) => {
    if (node.type === 'SUBBOT' && typeof node.config?.botId === 'string') ids.add(node.config.botId);
  });
  /** @type {Record<string, { definition: BotDefinition }>} */
  const result = {};
  await Promise.all([...ids].map(async (id) => {
    try {
      const bot = await getBot(id);
      result[id] = { definition: bot.definition };
    } catch {
      // subbot que não carrega volta pelo ramo "returned" no teste
    }
  }));
  return result;
}

/**
 * @param {{ definition: BotDefinition, botId: string, botName: string, lookupData: BotLookupData, lookups: BotSummaryLookups, institutionName: string,
 *   onClose: () => void, onShowNode?: (nodeId: string) => void }} props
 */
export function BotTestPanel({ definition, botId, botName, lookupData, lookups, institutionName, onClose, onShowNode }) {
  const interpret = botInterpreter();
  const [scenario, setScenario] = useState(/** @type {BotTestScenarioKey} */ ('CURRENT_FAMILY'));
  const [state, setState] = useState(/** @type {BotTestState | null} */ (null));
  const [draft, setDraft] = useState('');
  const [tab, setTab] = useState('path');
  const [isStarting, setIsStarting] = useState(false);
  const chatRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const index = indexBotTree(definition);
  const teamIds = (lookupData.teams ?? []).map((team) => team.id);
  const teamNames = Object.fromEntries((lookupData.teams ?? []).map((team) => [team.id, team.name]));

  const start = async (/** @type {string} */ firstMessage) => {
    if (!interpret) return;
    setIsStarting(true);
    const botsById = await loadSubbots(definition);
    setState(startBotTest({ definition, botId, botName, scenario, interpret, institutionName, teamIds, teamNames, botsById, firstMessage }));
    setIsStarting(false);
  };
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight });
  }, [state?.messages.length]);

  /** @param {{ id: string, title: string } | null} option @param {string} [text] */
  const answer = (option, text) => {
    if (!interpret) return;
    if (!state) {
      start(text ?? option?.title ?? '');
      return;
    }
    setState(answerBotTest(state, interpret, { text: option?.title ?? text ?? '', interactiveId: option?.id ?? null }));
  };
  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    answer(null, text);
  };
  const options = state ? lastBotOptions(state) : [];
  const nodeTitle = (/** @type {string} */ nodeId) => {
    const node = index.get(nodeId)?.node;
    return node ? describeBotNode(node, lookups).title : t('bots.test.otherBotNode');
  };

  return html`<${FlowSidePanel} title=${t('bots.test.title')} help=${t('bots.test.help')} onClose=${onClose} panelKey="test" testId="bot-test-panel"
    backLabel=${t('bots.panel.backToBot')}
    footer=${html`<${Button} title=${t('bots.test.restart')} icon="IconRefresh" variant="secondary" size="small" disabled=${!state} onClick=${() => {
      setState(null);
      setDraft('');
    }} />
      <${Button} title=${t('bots.panel.close')} variant="tertiary" size="small" onClick=${onClose} />`}>
    ${!interpret
      ? html`<${FlowNotice} tone="warning" icon="IconAlertTriangle">${t('bots.test.unavailable')}<//>`
      : html`
        <${BotSelectField} label=${t('bots.test.scenario')} value=${scenario} placeholder=${null} testId="bot-test-scenario"
          options=${BOT_TEST_SCENARIOS.map((value) => ({ value, label: t(`bots.test.scenarios.${value}.name`) }))}
          onChange=${(value) => {
            setScenario(/** @type {BotTestScenarioKey} */ (value ?? 'CURRENT_FAMILY'));
            setState(null);
          }} hint=${t(`bots.test.scenarios.${scenario}.description`)} />
        ${scenario === 'UNKNOWN_NUMBER' ? html`<p class="flow-field-hint">${t('bots.test.weakDataHint', BOT_TEST_WEAK_DATA)}</p>` : null}
        <p class="flow-field-hint">${t('bots.test.codeHint', { code: BOT_TEST_STRONG_CODE })}</p>
        <div class="bot-chat" data-testid="bot-test-chat">
          <div class="bot-chat-surface bot-chat-scroll" ref=${chatRef} aria-live="polite">
            ${!state
              ? html`<p class="bot-chat-empty">${t('bots.test.startHint')}</p>`
              : state.messages.map((message) => (message.direction === 'INCOMING'
                ? html`<div key=${message.id} class="bot-bubble-wrap" data-direction="incoming"><div class="bot-bubble" data-direction="incoming"><p class="bot-bubble-text">${message.text}</p></div></div>`
                : message.direction === 'NOTE'
                  ? html`<p key=${message.id} class="bot-chat-note">${t(message.noteKey ?? 'bots.test.integrationSimulated', message.noteParams ?? {})}</p>`
                  : html`<${BotMessagePreview} key=${message.id} model=${chatBubbleModel(message)} compact
                      onOption=${options.length > 0 && message.options === options ? answer : null} optionIds=${message.options.map((option) => option.id)} />`))}
            ${state?.ended
              ? html`<p class="bot-chat-ended" data-testid="bot-test-ended">${t('bots.test.ended', { status: t(`bots.runs.statuses.${state.ended.status}`), reason: botReasonText('END', state.ended.reason) })}</p>`
              : null}
            ${state?.error ? html`<p class="flow-field-error" role="alert">${t('bots.test.error', { message: state.error })}</p>` : null}
          </div>
          ${state && !state.ended && state.waiting
            ? html`<div class="bot-chat-wait">
                <span>${t('bots.test.waiting')}</span>
                ${definition.settings.reminderAfterMinutes ? html`<${Button} title=${t('bots.test.reminder')} variant="tertiary" size="small"
                  onClick=${() => interpret && setState(resumeBotTest(state, interpret, 'REMINDER'))} />` : null}
                <${Button} title=${t('bots.test.timeout')} variant="tertiary" size="small" onClick=${() => interpret && setState(resumeBotTest(state, interpret, 'TIMEOUT'))} />
              </div>`
            : null}
          <form class="bot-chat-composer" onSubmit=${(/** @type {Event} */ event) => {
            event.preventDefault();
            send();
          }}>
            <input class="text-input" value=${draft} placeholder=${state ? t('bots.test.answerPlaceholder') : t('bots.test.firstMessagePlaceholder')}
              aria-label=${t('bots.test.answerPlaceholder')} disabled=${Boolean(state?.ended) || isStarting} data-testid="bot-test-input"
              onInput=${(/** @type {InputEvent} */ event) => setDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
            <button type="submit" class="bot-chat-send" aria-label=${t('bots.test.send')} disabled=${!draft.trim() || Boolean(state?.ended) || isStarting}>
              <${Icon} name="IconSend" size=${16} />
            </button>
          </form>
          ${!state ? html`<${Button} title=${t('bots.test.start')} icon="IconPlayerPlay" accent="blue" size="small" isLoading=${isStarting} onClick=${() => start(draft.trim() || t('bots.test.defaultFirstMessage'))}
            data-testid="bot-test-start" />` : null}
        </div>
        ${state
          ? html`<${TabList} activeTabId=${tab} onChange=${setTab}
              tabs=${[{ id: 'path', title: t('bots.test.tabs.path') }, { id: 'variables', title: t('bots.test.tabs.variables') }, { id: 'summary', title: t('bots.test.tabs.summary') }]} />
            ${tab === 'path' && state.steps.length === 0 ? html`<p class="flow-field-hint">${t('bots.test.noSteps')}</p>` : null}
            ${tab === 'path' && state.steps.length > 0
              ? html`<ol class="bot-test-steps" data-testid="bot-test-steps">${state.steps.map((step, position) => {
                  const node = index.get(step.nodeId)?.node ?? null;
                  return html`<li key=${position} data-status=${step.status}>
                    <button type="button" class="flow-link-button" onClick=${() => onShowNode?.(step.nodeId)} disabled=${!node}>${nodeTitle(step.nodeId)}</button>
                    <span class="bot-test-step-status">${t(`bots.stepStatuses.${step.status}`)}${step.branchKey && node ? ` · ${botBranchLabel(node, step.branchKey)}` : ''}</span>
                    ${step.reason ? html`<span class="bot-test-step-reason">${botReasonText('STEP', step.reason)}</span>` : null}
                  </li>`;
                })}</ol>`
              : null}
            ${tab === 'variables'
              ? (() => {
                  const variables = testVariables(state, t('bots.test.informed'));
                  return variables.length === 0
                    ? html`<p class="flow-field-hint">${t('bots.test.noVariables')}</p>`
                    : html`<dl class="bot-test-variables">${variables.map((variable) => html`<dt key=${`${variable.name}-name`}><code>${`bot.${variable.name}`}</code></dt><dd key=${`${variable.name}-value`}>${variable.value}</dd>`)}</dl>`;
                })()
              : null}
            ${tab === 'summary'
              ? (() => {
                  const lines = testHandoffSummary(state, { titleOf: (node) => describeBotNode(node, lookups).title, informedText: t('bots.test.informed') });
                  return lines.length === 0
                    ? html`<p class="flow-field-hint">${t('bots.test.noSummary')}</p>`
                    : html`<pre class="bot-test-summary" data-testid="bot-test-summary">${lines.join('\n')}</pre>`;
                })()
              : null}`
          : null}
        <p class="flow-field-hint">${t('bots.test.nothingSent')}</p>`}
  <//>`;
}
